// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {
    endsWith,
    get,
    includes,
    isArray,
    isEmpty,
    isFunction,
    map,
    noop,
    pull,
    toUpper,
    trim,
    uniqueId,
    without,
} from 'lodash';
import classnames from 'classnames';

// Core Libs
import {formatDate} from '../lib/core/date';
import {isEqualJson} from '../lib/core/object';
import validators from '../lib/core/validators';

// Components
import FormFieldErrors from '../components/core/form-field-errors';

// Contexts
import {FormContext} from '../contexts/form-context';

// Styles
import './with-form-field.scss';

const withFormField = (WrappedComponent, type) => {
    class WithFormField extends Component {
        // eslint-disable-next-line react/destructuring-assignment
        inputId = this.props.id || uniqueId('with-form-field');

        state = {
            changed: false,
        };

        componentDidMount() {
            const {name, value} = this.props;

            if (this.shouldRenderField()) {
                if (this.shouldSetFieldValue()) {
                    this.setFieldValue(name, value);
                }

                this.validateField();
            }
        }

        componentDidUpdate(prevProps) {
            if (this.shouldRenderField(this.props)) {
                if (!this.shouldRenderField(prevProps)) {
                    this.setDefaultValue(this.props);
                }

                this.validateField(this.props);
            } else {
                this.unregisterField(this.props);
            }
        }

        onSubmit(event) {
            const {onSubmit} = this.context;

            this.setState({changed: false});
            onSubmit(event);
        }

        getClass() {
            const {className} = this.props;

            return classnames(
                {
                    'with-form-field': true,
                },
                className
            );
        }

        getElementValue(element) {
            const {trim: trimProp, type, uppercase} = this.props;
            const valueByType = {
                checkbox: () => (element.checked && element.value) || '',
                datetime: () => formatDate(element.value, 'YYYY-MM-DDTHH:mm:ssZ'),
            };
            let value = valueByType[type] ? valueByType[type]() : element.value;

            if (trimProp) {
                value = trim(value);
            }

            if (uppercase) {
                value = toUpper(value);
            }

            return value;
        }

        getFieldErrors(props = this.props) {
            const {fieldErrors: {[props.name]: fieldErrors = []} = {}} = this.getFormData(props);

            return fieldErrors;
        }

        getFieldValue(props = this.props) {
            const {formValues: formValuesProp, getFieldValue, name, type, value} = props;
            const defaultValue = '';
            const formData = this.getFormData(props);
            let fieldValue;

            if (isFunction(getFieldValue)) {
                fieldValue = getFieldValue(formData, name, value, type);
            } else if (type === 'checkbox' && endsWith(name, '[]')) {
                const formValues =
                    formValuesProp || get(formData, ['values', name.substring(0, name.length - 2)]) || [];

                fieldValue = formValues.includes(value) ? value : '';
            } else {
                fieldValue = get(formData.values, name, defaultValue);
            }

            return fieldValue === null ? '' : fieldValue;
        }

        getFormData(props = this.props) {
            const {formName} = this.context;

            return props.formData[formName] || {};
        }

        getWrappedProps() {
            const {onSubmit} = this.context;
            const {value} = this.props;

            return {
                ...this.props,
                className: 'with-form-field--component',
                defaultValue: value,
                onBlur: this.handleBlur,
                onChange: this.handleChange,
                onClick: this.handleClick,
                onSubmit,
                value: this.getFieldValue(),
            };
        }

        setDefaultValue(props = this.props) {
            const {defaultValue, name} = props;
            const formData = this.getFormData(props);

            if (defaultValue !== undefined && get(formData.values, name) === undefined) {
                this.setFieldValue(name, defaultValue);
            }
        }

        setFieldValue(name, value) {
            const {setFieldValue} = this.context;
            const {formValues: formValuesProp, operator, type, value: valueProp} = this.props;

            let fieldName = name;
            let fieldValue =
                type !== 'number' || value === '' || value === undefined || value === null ? value : +value;

            if (endsWith(name, '[]')) {
                fieldName = name.substring(0, name.length - 2);

                if (fieldName.length) {
                    const formData = this.getFormData(this.props);
                    const formValues = formValuesProp || get(formData, ['values', fieldName]) || [];

                    if (type === 'checkbox') {
                        fieldValue = valueProp;
                    }

                    if (value && !formValues.includes(fieldValue)) {
                        fieldValue = [...formValues, fieldValue];
                    } else if (!value && formValues.includes(fieldValue)) {
                        fieldValue = pull([...formValues], fieldValue);
                    }
                }
            }

            if (isArray(value)) {
                fieldValue = without(fieldValue, false, null, '', undefined, NaN);
            }

            return setFieldValue(fieldName, fieldValue, operator);
        }

        handleBlur = (event) => {
            const {onBlur = noop, submitOnBlur} = this.props;
            const {changed} = this.state;

            onBlur(event);

            if (submitOnBlur && changed) {
                this.onSubmit(event);
            }
        };

        handleChange = (event, setValue) => {
            const {onChange = noop, name, submitOnChange} = this.props;
            const fieldValue = setValue !== undefined ? setValue : this.getElementValue(event.target);

            if (get(event, 'persist')) {
                event.persist();
            }

            this.setState({changed: true});

            this.setFieldValue(name, fieldValue).then(() => {
                if (submitOnChange) {
                    this.onSubmit(event);
                }

                onChange(event);
            });
        };

        handleClick = (event) => {
            const {onClick = noop} = this.props;

            onClick(event);
        };

        handleKeyDown = (event) => {
            const {submitOnBlur} = this.props;

            if (event.which !== 13) {
                event.stopPropagation();
            } else if (submitOnBlur) {
                this.onSubmit(event);
            }
        };

        hasFieldErrors(props = this.props) {
            return this.getFieldErrors(props).length;
        }

        shouldRenderField(props = this.props) {
            const {name, visible = true} = props;

            const formData = this.getFormData(props);
            const values = get(formData, 'values', {});
            const visibleFields = get(formData, 'visibleFields', []);
            const constFields = get(formData, 'constantFields', []);

            return isFunction(visible)
                ? visible(values, name, this.getFieldValue(props))
                : !isEmpty(constFields) && includes(constFields, name)
                  ? !!visible
                  : !isEmpty(visibleFields)
                    ? includes(visibleFields, name)
                    : !!visible;
        }

        shouldSetFieldValue() {
            const {defaultValue, value, type} = this.props;

            return type === 'checkbox' ? defaultValue === value : value !== undefined;
        }

        unregisterField(props = this.props) {
            const {name} = props;
            const {formName, unregisterFieldErrors, unregisterFieldValue} = this.context;
            const formData = this.getFormData(props);

            if (get(formData.values, name) !== undefined) {
                unregisterFieldValue(formName, name);
            }

            if (this.hasFieldErrors(props)) {
                unregisterFieldErrors(formName, name);
            }
        }

        validateField(props = this.props) {
            const {name, requiredFields, validate = []} = props;
            const {formName, setFieldErrors, unregisterFieldErrors} = this.context;
            const formDataValues = get(this.getFormData(props), 'values');
            // const valueIsSame = isEqualJson(this.getFieldValue(), this.getFieldValue(props));
            const fieldErrors = [];

            if (validate.length && formDataValues) {
                const fieldValue = this.getFieldValue(props);

                map(validate, (validator) => {
                    const fieldError = isFunction(validator)
                        ? validator(formDataValues, name, fieldValue)
                        : validators[validator](fieldValue); // eslint-disable-line import/namespace
                    // (valueIsSame) ? undefined :

                    if (fieldError) {
                        fieldErrors.push(fieldError);
                    }
                });

                if (fieldErrors.length && !isEqualJson(this.getFieldErrors(props), fieldErrors)) {
                    setFieldErrors(formName, name, fieldErrors);
                } else if (!fieldErrors.length && this.hasFieldErrors(props)) {
                    unregisterFieldErrors(formName, name);
                }
            }

            if (requiredFields) {
                let requiredFieldError;
                const requiredFieldErrors = [];
                let requiredFieldIsSet;

                map(requiredFields, (requiredField) => {
                    requiredFieldError = validators.required(get(formDataValues, requiredField));
                    requiredFieldIsSet = requiredFieldIsSet || !requiredFieldError;

                    if (requiredFieldError) {
                        requiredFieldErrors.push({
                            requiredField,
                            requiredFieldError: `If ${requiredFields.join(', ')} have a value, this field is required.`,
                        });
                    } else if (this.getFieldErrors(props).length) {
                        unregisterFieldErrors(formName, requiredField);
                    }
                });

                if (requiredFieldIsSet && requiredFieldErrors.length) {
                    map(requiredFieldErrors, (requiredFieldInfo) => {
                        setFieldErrors(formName, requiredFieldInfo.requiredField, [
                            requiredFieldInfo.requiredFieldError,
                        ]);
                    });
                } else {
                    map(requiredFields, (requiredField) => {
                        unregisterFieldErrors(formName, requiredField);
                    });
                }
            }
        }

        renderFieldErrors() {
            const {onlyShowFirstError} = this.props;

            let content = null;

            if (this.hasFieldErrors()) {
                content = <FormFieldErrors errors={this.getFieldErrors()} onlyShowFirstError={onlyShowFirstError} />;
            }

            return content;
        }

        render() {
            const {id, label, title} = this.props;
            const inputId = id || this.inputId;

            return this.shouldRenderField() ? (
                <div
                    className={this.getClass()}
                    role="button"
                    tabIndex="-1"
                    title={title}
                    onKeyDown={this.handleKeyDown}
                >
                    <label className="with-form-field--label" htmlFor={inputId}>
                        {label}
                    </label>
                    <WrappedComponent {...this.getWrappedProps()} id={inputId} />
                    {this.renderFieldErrors()}
                </div>
            ) : null;
        }
    }

    WithFormField.propTypes = {
        className: PropTypes.string,
        defaultValue: PropTypes.oneOfType([PropTypes.array, PropTypes.number, PropTypes.string]),
        formData: PropTypes.object.isRequired,
        formValues: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        getFieldValue: PropTypes.func,
        id: PropTypes.string,
        label: PropTypes.string,
        lockingServiceKey: PropTypes.string,
        name: PropTypes.string.isRequired,
        onBlur: PropTypes.func,
        onChange: PropTypes.func,
        onClick: PropTypes.func,
        onlyShowFirstError: PropTypes.bool,
        operator: PropTypes.string,
        requiredFields: PropTypes.array,
        submitOnBlur: PropTypes.bool,
        submitOnChange: PropTypes.bool,
        title: PropTypes.string,
        trim: PropTypes.bool,
        type: PropTypes.string,
        uppercase: PropTypes.bool,
        validate: PropTypes.array,
        value: PropTypes.oneOfType([PropTypes.array, PropTypes.number, PropTypes.string]),
        visible: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    };

    WithFormField.contextType = FormContext;

    WithFormField.displayName = `WithFormField(${getDisplayName(WrappedComponent)})`;

    const mapStateToProps = (state) => ({
        formData: state.formData,
        type
    });

    return connect(mapStateToProps)(WithFormField);
};

function getDisplayName(WrappedComponent) {
    return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

export default withFormField;
